import { defineStore } from 'pinia';
import api from '@/services/api';
import { useAuthStore } from './auth';

// Data is considered stale after 5 minutes
const STALE_TIME = 5 * 60 * 1000;

export const useCirclesStore = defineStore('circles', {
	state: () => ({
		circles: [],
		currentCircle: null,
		loading: false,
		error: null,
		lastFetched: null
	}),

	getters: {
		allCircles: (state) => state.circles,
		circleById: (state) => (id) => state.circles.find((circle) => circle.id === id),
		getCurrentCircle: (state) => state.currentCircle,
		isLoading: (state) => state.loading,
		hasError: (state) => !!state.error,
		getError: (state) => state.error,
		activeCircle: (state) => state.currentCircle,
		isStale: (state) => !state.lastFetched || Date.now() - state.lastFetched > STALE_TIME,
		hasData: (state) => state.circles.length > 0,
		userCircles() {
			const authStore = useAuthStore();
			const userId = authStore.userId;
			if (!userId) return [];

			return this.circles.filter((circle) => {
				// Check memberships for user's membership in this circle
				const isMember = circle.memberships?.some((membership) => {
					const memberUserId = membership.user?.id || membership.user;
					return memberUserId === userId;
				});
				return isMember;
			});
		},
		// Helper to get user's role in a specific circle
		getUserRoleInCircle: (state) => (circleId, userId) => {
			const circle = state.circles.find((t) => t.id === circleId);
			if (!circle || !circle.memberships) return null;
			const membership = circle.memberships.find((m) => {
				const memberUserId = m.user?.id || m.user;
				return memberUserId === userId;
			});
			return membership?.role || null;
		}
	},

	actions: {
		setCircles(circles) {
			this.circles = circles;
		},

		setCurrentCircle(circle) {
			this.currentCircle = circle;
		},

		setLoading(loading) {
			this.loading = loading;
		},

		setError(error) {
			this.error = error;
		},

		addCircle(circle) {
			this.circles.push(circle);
		},

		updateCircle(updatedCircle) {
			const index = this.circles.findIndex((circle) => circle.id === updatedCircle.id);
			if (index !== -1) {
				this.circles.splice(index, 1, updatedCircle);
			}
			if (this.currentCircle && this.currentCircle.id === updatedCircle.id) {
				this.currentCircle = updatedCircle;
			}
		},

		removeCircle(circleId) {
			this.circles = this.circles.filter((circle) => circle.id !== circleId);
			if (this.currentCircle && this.currentCircle.id === circleId) {
				this.currentCircle = null;
			}
		},

		setActiveCircle(circleId) {
			if (circleId == null) {
				this.currentCircle = null;
				localStorage.removeItem('activeCircleId');
				return;
			}
			const circle = this.circles.find((t) => String(t.id) === String(circleId));
			if (circle) {
				this.currentCircle = circle;
				localStorage.setItem('activeCircleId', String(circle.id));
			}
		},

		async fetchCircles() {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getCircles();
				this.setCircles(response.data);
				this.lastFetched = Date.now();
				return response.data;
			} catch (error) {
				this.setError(error.message || 'Failed to fetch circles');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		// Smart fetch - only fetches if data is missing or stale
		async ensureLoaded(force = false) {
			// Skip if already loading
			if (this.loading) return this.circles;

			// Skip if we have fresh data and not forcing
			if (!force && this.hasData && !this.isStale) {
				return this.circles;
			}

			return this.fetchCircles();
		},

		async fetchCircle(circleId) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getCircle(circleId);
				this.setCurrentCircle(response.data);
				return response.data;
			} catch (error) {
				this.setError(error.message || `Failed to fetch circle with ID ${circleId}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async createCircle(circleData) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.createCircle(circleData);
				const newCircle = response.data;
				this.addCircle(newCircle);
				return newCircle;
			} catch (error) {
				this.setError(error.message || 'Failed to create circle');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async updateCircleAction({ id, circleData }) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.updateCircle(id, circleData);
				const updatedCircle = response.data;
				this.updateCircle(updatedCircle);
				return updatedCircle;
			} catch (error) {
				this.setError(error.message || `Failed to update circle with ID ${id}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async deleteCircle(circleId) {
			this.setLoading(true);
			this.setError(null);

			try {
				await api.deleteCircle(circleId);
				this.removeCircle(circleId);
			} catch (error) {
				this.setError(error.message || `Failed to delete circle with ID ${circleId}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async removeMemberFromCircle({ circleId, membershipId }) {
			this.setLoading(true);
			this.setError(null);

			try {
				await api.deleteCircleMember(membershipId);
				// Refresh circle data to get updated memberships
				await this.fetchCircles();
			} catch (error) {
				this.setError(error.message || 'Failed to remove member from circle');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async setActiveCircleAction(circleId) {
			if (!circleId) {
				this.setActiveCircle(null);
				return;
			}

			// If circle is not in state yet, fetch it first
			if (!this.circles.some((t) => t.id === circleId)) {
				await this.fetchCircles();
			}

			this.setActiveCircle(circleId);
		},

		// Resolve the active circle from localStorage (if still valid) or default to
		// the user's first circle. Always ensures an active circle is set when the
		// user belongs to at least one circle.
		async loadActiveCircle() {
			const authStore = useAuthStore();
			if (!authStore.userId) return;

			await this.ensureLoaded();

			const myCircles = this.userCircles;
			if (myCircles.length === 0) {
				this.currentCircle = null;
				localStorage.removeItem('activeCircleId');
				return;
			}

			const savedCircleId = localStorage.getItem('activeCircleId');
			const savedIsValid = savedCircleId && myCircles.some((t) => String(t.id) === String(savedCircleId));
			const targetId = savedIsValid ? savedCircleId : myCircles[0].id;
			this.setActiveCircle(targetId);
		}
	}
});
